import { DragHandleIcon } from '@chakra-ui/icons'
import { Box, Button, Divider, Flex, IconButton, Text, VStack } from '@chakra-ui/react'
import { ScrollableBodyContainer } from '@components/Layout/ScrollableBodyContainer'
import {
  FieldMap,
  FieldTypes,
  ListField,
  PregnancyClinicalRationale,
  TextAreaField,
  TextField,
  WithId,
} from '@hb/shared'
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
} from '@hello-pangea/dnd'
import { useCallback, useState } from 'react'
import { AddItem } from '../../../AddItem'
import { ActionButton, DeleteButton } from '../../../Buttons'
import { GenericEditModal } from '../../../DataView/GenericEditModal'
import { Editable } from '../../../forms/Input'
import { CollapseHorizontal } from '../../../shared'
import { PregnancyClinicalRationalesProvider } from './context'
import { usePregnancyClinicalRationales } from './hooks'
import { TestFeatureModal } from './TestFeatureModal'

const rationaleCategoryNameField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Category Name',
}

const rationaleKeywordField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Keyword',
}

const rationaleKeywordsField: ListField = {
  name: 'keywords',
  itemFields: rationaleKeywordField,
  itemName: 'Keyword',
}

const rationaleCategoryRationaleField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Rationale (reason why the category is relevant to the pregnancy)',
}

const rationaleField: FieldMap = {
  name: 'Pregnancy Clinical Rationale',
  children: {
    category: rationaleCategoryNameField,
    keywords: rationaleKeywordsField,
    rationale: rationaleCategoryRationaleField,
  },
}

const RationaleKeyword = ({
  keyword,
  onDelete,
}: {
  keyword: string
  onDelete: () => Promise<void>
}) => {
  const [hovered, setHovered] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const handleDelete = useCallback(async () => {
    setDeleteLoading(true)
    try {
      await onDelete()
    } finally {
      setDeleteLoading(false)
    }
  }, [onDelete])
  return (
    <Flex
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      px={2}
      cursor="pointer"
      bg="green.100"
      _dark={{ bg: 'green.800' }}
      borderRadius={4}
      align="center">
      <Text fontSize="sm" fontWeight={500}>
        {keyword}
      </Text>
      <CollapseHorizontal in={hovered || deleteLoading} width={30}>
        <Box pl={2}>
          <DeleteButton itemName={keyword} onDelete={handleDelete} />
        </Box>
      </CollapseHorizontal>
    </Flex>
  )
}

const PregnancyClinicalRationaleKeywords = ({
  rationale,
}: {
  rationale: WithId<PregnancyClinicalRationale>
}) => {
  const { handleUpdateCategoryKeywords } = usePregnancyClinicalRationales()
  const handleDeleteKeyword = useCallback(
    async (keyword: string) => {
      const handleError = (error: string) => {
        throw new Error(error)
      }
      try {
        const result = await handleUpdateCategoryKeywords(
          rationale.id,
          rationale.keywords.filter(k => k !== keyword),
        )
        if (result.error) {
          handleError(result.error)
        }
      } catch (error) {
        handleError(error as string)
      }
    },
    [handleUpdateCategoryKeywords, rationale.id, rationale.keywords],
  )

  const handleAddKeyword = useCallback(
    async (keyword: string) => {
      if (rationale.keywords.map(k => k.toLowerCase()).includes(keyword.toLowerCase())) {
        console.log('keyword already exists')
        return { error: 'Keyword already exists' }
      }
      return handleUpdateCategoryKeywords(rationale.id, [...rationale.keywords, keyword])
    },
    [handleUpdateCategoryKeywords, rationale.id, rationale.keywords],
  )

  return (
    <Flex w="100%" gap={2} py={2}>
      <Text pt={0.5} fontWeight={500}>
        Keywords
      </Text>
      <Flex flexWrap="wrap" gap={2} flex={1} minWidth={0}>
        {rationale.keywords.map(keyword => (
          <RationaleKeyword
            key={keyword}
            keyword={keyword}
            onDelete={() => handleDeleteKeyword(keyword)}
          />
        ))}
      </Flex>
      <AddItem placeholder="Keyword" itemName="Keyword" onAdd={handleAddKeyword} />
    </Flex>
  )
}

const PregnancyClinicalRationaleCard = ({
  rationale,
  dragHandleProps,
}: {
  rationale: WithId<PregnancyClinicalRationale>
  dragHandleProps?: DraggableProvidedDragHandleProps | null
}) => {
  const { handleUpdateCategoryName, handleUpdateCategoryRationale, handleDeleteCategory } =
    usePregnancyClinicalRationales()

  return (
    <Flex
      w="100%"
      direction="column"
      px={4}
      borderRadius={4}
      shadow="md"
      bg="white"
      _dark={{ bg: 'gray.900' }}
      py={2}>
      <Flex align="center" pb={1} gap={2}>
        {dragHandleProps ? (
          <IconButton
            aria-label="Reorder"
            boxShadow="1px 1px 4px #00000044"
            bg="white"
            size="xs"
            icon={<DragHandleIcon w={3} />}
            {...dragHandleProps}
          />
        ) : null}
        <Flex flex={1} minWidth={0} align="center">
          <Editable
            field={rationaleCategoryNameField}
            value={rationale.category}
            onSubmit={v => handleUpdateCategoryName(rationale.id, v)}
            dataCellProps={{
              fontSize: 'lg',
              fontWeight: 500,
            }}
          />
        </Flex>
        <DeleteButton itemName="Category" onDelete={() => handleDeleteCategory(rationale.id)} />
      </Flex>
      <Divider borderColor="gray.200" _dark={{ borderColor: 'gray.700' }} />
      <PregnancyClinicalRationaleKeywords rationale={rationale} />
      <Divider borderColor="gray.200" _dark={{ borderColor: 'gray.700' }} />
      <Flex w="100%" gap={2}>
        <Text pt={1} fontWeight={500}>
          Rationale
        </Text>
        <Editable
          field={rationaleCategoryRationaleField}
          value={rationale.rationale}
          onSubmit={v => handleUpdateCategoryRationale(rationale.id, v)}
          dataCellProps={{
            fontSize: 'md',
            py: 0,
          }}
        />
      </Flex>
    </Flex>
  )
}
const PregnancyClinicalRationalesContent = () => {
  const { rationales, handleAddCategory, handleDragEnd } = usePregnancyClinicalRationales()
  const [addOpen, setAddOpen] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  return (
    <>
      <TestFeatureModal isOpen={testOpen} onClose={() => setTestOpen(false)} />
      <ScrollableBodyContainer
        header={
          <Flex
            align="center"
            w="100%"
            bg="gray.50"
            _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}
            borderBottomWidth={1}>
            <Text
              py={2}
              px={4}
              fontSize="xl"
              fontWeight={600}
              color="gray.700"
              _dark={{ color: 'gray.200' }}
              flex={1}>
              Pregnancy Clinical Rationales
            </Text>
            <ActionButton
              size="sm"
              mr={2}
              onClick={e => {
                e.stopPropagation()
                setTestOpen(true)
              }}>
              Test Feature
            </ActionButton>
            <Button
              size="sm"
              colorScheme="green"
              mr={2}
              onClick={e => {
                e.stopPropagation()
                setAddOpen(true)
              }}>
              + Add Rationale
            </Button>
          </Flex>
        }
        flexProps={{ bg: 'gray.100', _dark: { bg: 'gray.700' } }}
        body={
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="pregnancy-clinical-rationales">
              {provided => (
                <VStack
                  p={2}
                  spacing={2}
                  w="100%"
                  ref={provided.innerRef}
                  {...provided.droppableProps}>
                  {rationales.map((rationale, index) => (
                    <Draggable key={rationale.id} draggableId={rationale.id} index={index}>
                      {(draggableProvided, draggableSnapshot) => (
                        <Box
                          w="100%"
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          boxShadow={draggableSnapshot.isDragging ? 'lg' : undefined}>
                          <PregnancyClinicalRationaleCard
                            rationale={rationale}
                            dragHandleProps={draggableProvided.dragHandleProps}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </VStack>
              )}
            </Droppable>
          </DragDropContext>
        }
      />
      <GenericEditModal<PregnancyClinicalRationale>
        field={rationaleField}
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(data, _onProgress) => handleAddCategory(data)}
        closeOnSubmit
      />
    </>
  )
}

export const PregnancyClinicalRationalesView = () => (
  <PregnancyClinicalRationalesProvider>
    <PregnancyClinicalRationalesContent />
  </PregnancyClinicalRationalesProvider>
)
